import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface AdminPaginationProps {
  pagination: PaginationData;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
}

const AdminPagination: React.FC<AdminPaginationProps> = ({
  pagination,
  onPageChange,
  onLimitChange
}) => {
  const { page, limit, total, totalPages } = pagination;

  // Calculate displayed range
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    let startPage = Math.max(1, page - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    // Adjust start if we're near the end
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="px-4 py-3 flex items-center justify-between border-t border-white/[.08] sm:px-6">
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="sf-btn-secondary px-4 py-2 text-sm"
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="sf-btn-secondary ml-3 px-4 py-2 text-sm"
        >
          Next
        </button>
      </div>
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4">
          <p className="text-sm text-fg-muted">
            Showing <span className="font-semibold text-fg-bright">{startItem}</span> to{' '}
            <span className="font-semibold text-fg-bright">{endItem}</span> of{' '}
            <span className="font-semibold text-fg-bright">{total}</span> results
          </p>

          {onLimitChange && (
            <div className="flex items-center space-x-2">
              <label htmlFor="limit" className="sf-label-mono">
                Per page:
              </label>
              <select
                id="limit"
                value={limit}
                onChange={(e) => onLimitChange(parseInt(e.target.value))}
                className="rounded-lg border border-white/10 bg-white/[.04] px-2 py-1 text-sm text-fg focus:border-neon-pink/40 focus:outline-none"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          )}
        </div>

        <div>
          <nav className="inline-flex items-center gap-1.5" aria-label="Pagination">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className="sf-btn-secondary p-2"
            >
              <span className="sr-only">Vorige</span>
              <ChevronLeft className="h-4 w-4" />
            </button>

            {pageNumbers.map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                className={`inline-flex items-center justify-center rounded-btn px-3.5 py-2 text-sm font-semibold transition-all duration-200 ${
                  pageNum === page
                    ? 'bg-neon-gradient font-bold text-ink shadow-neon-pink'
                    : 'border border-white/10 bg-white/5 text-fg-card hover:bg-white/10'
                }`}
              >
                {pageNum}
              </button>
            ))}

            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
              className="sf-btn-secondary p-2"
            >
              <span className="sr-only">Volgende</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default AdminPagination;
